<template>
	<xyz-transition appear xyz="up-100%">
		<header
			class="page-header xyz-out-nested"
			:class="{ 'header--collapsed': headerCollapsed, 'patterned-bg': !themeLight, 'header--light': themeLight }"
			v-random-bg
		>
			<div class="page-header__top">
				<button class="header-logo logo-button" @click="onLogoClick">
					<icon-ya v-if="!themeLight"></icon-ya>
					<icon-ya-color v-if="themeLight"></icon-ya-color>
				</button>

				<slot name="header-top"></slot>

				<user-menu :theme-light="!themeLight" class="ml-xxs"></user-menu>
			</div>

			<div class="page-header__main" v-if="$slots['header-main']">
				<div class="page-header__content">
					<slot name="header-main"></slot>
				</div>
			</div>

			<div class="page-header__notifications" v-if="$slots['header-notifications']">
				<slot name="header-notifications"></slot>
			</div>
		</header>
	</xyz-transition>
</template>

<script>
import IconYa from '@/components/icons/IconYa.svg'
import IconYaColor from '@/components/icons/IconYaColor.svg'
import UserMenu from '@/components/menus/UserMenu'
import { mapState } from 'vuex'

export default {
	name: 'PageHeader',
	props: {
		themeLight: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		IconYa,
		IconYaColor,
		UserMenu,
	},
	data() {
		return {
			scrollBuffer: 16 * 4,
			lastScrollTop: 0,
			headerCollapsed: false,
		}
	},
	computed: {
		...mapState({
			desktopMode: (state) => state.desktopMode,
		}),
	},
	watch: {
		desktopMode() {
			this.updateWindowScroll()
		},
		hasMainSlot() {
			this.updateWindowScroll()
		},
	},
	methods: {
		onLogoClick() {
			this.$emit('logo-click')
		},
		updateHeaderOffset() {
			document.documentElement.style.setProperty('--header-offset', `${this.$el.clientHeight}px`)
		},
		clearHeaderOffset() {
			document.documentElement.style.setProperty('--header-offset', '0px')
		},
		updateWindowScroll() {
			this.headerCollapsed = false
			this.lastScrollTop = 0
			if (!this.desktopMode && this.$slots['header-main']) {
				window.addEventListener('scroll', this.onWindowScroll)
			} else {
				window.removeEventListener('scroll', this.onWindowScroll)
			}
		},
		onWindowScroll() {
			const scrollTarget = document.scrollingElement || document.documentElement

			if (scrollTarget.scrollTop < this.scrollBuffer) {
				this.headerCollapsed = false
			} else if (scrollTarget.scrollTop > scrollTarget.scrollHeight - scrollTarget.clientHeight - this.scrollBuffer) {
				this.headerCollapsed = true
			} else {
				if (scrollTarget.scrollTop < this.lastScrollTop) {
					this.headerCollapsed = false
				} else if (scrollTarget.scrollTop > this.lastScrollTop) {
					this.headerCollapsed = true
				}
			}

			this.lastScrollTop = scrollTarget.scrollTop
		},
	},
	mounted() {
		window.addEventListener('resize', this.updateHeaderOffset)
		this.updateWindowScroll()
		this.updateHeaderOffset()
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateHeaderOffset)
		window.removeEventListener('scroll', this.onWindowScroll)
		this.clearHeaderOffset()
	},
}
</script>

<style lang="scss" scoped>
.page-header {
	width: 100%;
	z-index: $z-nav;
	position: sticky;
	min-height: 3rem;
	top: 0rem;
	background-color: theme-color(600);
	transition: top 0.3s $ease-in-out-quad, background-color 0.4s $ease-in-out-quad;

	&.header--collapsed {
		top: -2.5rem;
	}

	&.header--light {
		background-color: rgba(255, 255, 255, 0.85);
		backdrop-filter: blur(4px);
	}

	@include media('>=laptop') {
		min-height: 4rem;
		flex-shrink: 0;
		background-size: 50%;
	}
}

.page-header__top {
	display: flex;
	align-items: center;
	padding: $spacing-xxs $spacing-s;
	padding-bottom: 0;
	min-height: 2.5rem;

	@include media('>=laptop') {
		position: absolute;
		top: 0;
		width: 100%;
		padding-top: $spacing-s;
	}
}

.header-logo {
	margin-right: auto;
}

.icon-ya {
	height: 1.75rem;
	width: auto;
}

.page-header__main {
	position: relative;
	width: 40rem;
	max-width: 100%;
	padding: $spacing-xxs $spacing-s;
	margin: 0 auto;

	@include media('>=laptop') {
		padding: $spacing-s;
	}

	@include media('>=large') {
		width: 45rem;
	}
}

.page-header__content {
	display: flex;
	align-items: center;
	width: 100%;
}

::v-deep {
	.page-header__title {
		display: flex;
		align-items: center;
		margin: 0;
		margin-right: auto;
		color: white;
		line-height: 1.75rem;
		min-height: 2rem;
		font-size: $fs-medium;
		font-weight: $fw-boldest;

		.header--light & {
			color: theme-color(700);
		}
	}
}
</style>
